import { useEffect, useRef, useState } from "react";
import { Sparkles, Banknote, Handshake, FileCheck2, CheckCircle2, Activity } from "lucide-react";

const KPIS = [
  { value: "₹4.82 Cr", label: "Receivables", green: false },
  { value: "₹1.96 Cr", label: "Outstanding", green: false },
  { value: "59.3%", label: "Recovery", green: true },
];
const BARS = [
  { h: 40, c: "#0ea5a4" }, { h: 60, c: "#0B3D91" }, { h: 48, c: "#0ea5a4" }, { h: 74, c: "#0B3D91" },
  { h: 58, c: "#0ea5a4" }, { h: 86, c: "#0B3D91" }, { h: 70, c: "#0ea5a4" }, { h: 96, c: "#16A34A" },
];
// Authentic RecoverOS collections activity — money moving, workflow progressing, health improving.
const EVENTS = [
  { Icon: Banknote, title: "Borrower paid ₹25,000", sub: "EMI · Loan #DEMO-10241" },
  { Icon: Handshake, title: "Promise to Pay received", sub: "₹40,000 by 28th · #DEMO-10188" },
  { Icon: FileCheck2, title: "Settlement awaiting approval", sub: "₹1,10,000 · #DEMO-10077" },
  { Icon: CheckCircle2, title: "Follow-up completed", sub: "Callback logged · #DEMO-10203" },
  { Icon: Activity, title: "Recovery Health Score improved", sub: "+2 pts this week" },
];
// Next Best Action recommendations (genuine prioritisation engine).
const ACTIONS = [
  ["Next Best Action: chase 12 high-recovery accounts", "est. ₹18.4L recoverable this week"],
  ["3 accounts likely to settle this week", "prioritised by recovery probability"],
  ["5 broken Promises to Pay need a call today", "flagged for follow-up"],
];

// Living "Portfolio Intelligence" panel for the hero. Presentational mock only — no data, no CTAs.
// KPIs & trend are static (always correct); the "living" motion is the real collections activity
// (payments, PTPs, settlements, follow-ups), the rotating Next Best Action and the ticking health score.
export const LivingDashboard = () => {
  const [health, setHealth] = useState(82);
  const [toastOn, setToastOn] = useState(false);
  const [eventIdx, setEventIdx] = useState(0);
  const [actionIdx, setActionIdx] = useState(0);
  const dashRef = useRef(null);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) { setToastOn(true); return; }
    const t = setTimeout(() => setToastOn(true), 1400);
    const eventLoop = setInterval(() => {
      setToastOn(true);
      setTimeout(() => setToastOn(false), 3400);
      setEventIdx((i) => (i + 1) % EVENTS.length);
    }, 6000);
    const actionLoop = setInterval(() => setActionIdx((i) => (i + 1) % ACTIONS.length), 5600);
    const healthLoop = setInterval(() => setHealth((h) => (Math.random() > 0.45 ? (h >= 88 ? 82 : h + 1) : h)), 4200);
    return () => { clearTimeout(t); clearInterval(eventLoop); clearInterval(actionLoop); clearInterval(healthLoop); };
  }, []);

  const onMove = (e) => {
    const el = dashRef.current;
    if (!el || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const r = el.getBoundingClientRect();
    const rx = (((e.clientY - r.top) / r.height) - 0.5) * -4;
    const ry = (((e.clientX - r.left) / r.width) - 0.5) * 6;
    el.style.transform = `perspective(900px) rotateX(${rx.toFixed(2)}deg) rotateY(${ry.toFixed(2)}deg)`;
  };
  const onLeave = () => { if (dashRef.current) dashRef.current.style.transform = "none"; };

  const ev = EVENTS[eventIdx];
  const [ah, as] = ACTIONS[actionIdx];

  return (
    <div className="relative" onMouseMove={onMove} onMouseLeave={onLeave} data-testid="living-dashboard">
      <div className="absolute -inset-4 -z-10 bg-[#0B3D91]/5" />
      {/* Live collections-activity toast */}
      <div className={`absolute -top-3.5 -right-4 z-10 flex items-center gap-2.5 rounded-xl bg-white px-3 py-2.5 text-[13px] font-bold text-slate-900 shadow-[0_16px_40px_rgba(11,61,145,0.22)] transition-all duration-500 ${toastOn ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2.5"}`} data-testid="dashboard-activity-toast">
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#16A34A]/15 text-[#16A34A]"><ev.Icon className="h-3.5 w-3.5" /></span>
        <span>{ev.title}<small className="block text-[11px] font-medium text-slate-400">{ev.sub}</small></span>
      </div>

      <div ref={dashRef} className="ros-dash-tilt overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_30px_80px_-44px_rgba(11,61,145,0.32),0_2px_8px_rgba(16,24,40,0.05)]">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3.5">
          <b className="text-sm text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Portfolio Intelligence</b>
          <div className="flex items-center gap-3.5">
            <span className="text-xs font-semibold text-slate-500">Health <b className="text-[#16A34A]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{health}</b></span>
            <span className="flex items-center gap-1.5 text-[11px] font-bold text-[#16A34A]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#16A34A] ros-live-dot" />Live
            </span>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2.5 p-4">
          {KPIS.map((k) => (
            <div key={k.label} className="rounded-xl border border-slate-100 bg-white/60 px-3 py-3">
              <div className={`mt-0.5 text-lg font-extrabold ${k.green ? "text-[#16A34A]" : "text-slate-900"}`} style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{k.value}</div>
              <div className="mt-0.5 text-[11px] text-slate-400">{k.label}</div>
            </div>
          ))}
        </div>

        <div className="mx-4 flex items-start gap-2.5 rounded-xl border border-[#2563eb]/20 bg-gradient-to-r from-[#2563eb]/10 to-[#0ea5a4]/10 px-3.5 py-3">
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-[#2563eb]/15 text-[#0B3D91]"><Sparkles className="h-3.5 w-3.5" /></span>
          <div>
            <b className="text-[13px] text-slate-900 ros-fade">{ah}</b>
            <small className="mt-0.5 block text-xs text-slate-500 ros-fade">{as}</small>
          </div>
        </div>

        <div className="m-4 rounded-xl border border-slate-100 bg-white/60 px-4 py-3.5">
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span>Recovery Trend</span><span className="font-bold text-[#16A34A]">+12.4%</span>
          </div>
          <div className="mt-2.5 flex h-16 items-end gap-2">
            {BARS.map((b, i) => (
              <div key={i} className="flex-1 rounded-t-sm" style={{ height: `${b.h}%`, background: b.c }} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
